import { notificationsConfigSchema } from '@bia-auction/shared-schema';
import { api } from './api';

/**
 * Browser Web Push wiring (M8, ADR 0021). Registers the service worker, subscribes with the
 * server's VAPID key (`GET /me/notifications/config`), and stores the subscription
 * (`PUT /me/push-subscription`). Every entry point **feature-detects and never throws** — on a
 * browser without the Push API (e.g. older iOS Safari) the UI simply hides the control.
 */

export type EnableResult = 'enabled' | 'blocked' | 'unsupported' | 'unconfigured' | 'failed';

/**
 * Whether the server has Web Push fully configured (VAPID present). `GET /me/notifications/config`
 * returns `vapidPublicKey: null` unless public+private+subject are all set, so the bell can hide
 * itself rather than offering an opt-in that would only ever return `unconfigured`.
 */
export async function isPushConfigured(): Promise<boolean> {
  try {
    const config = await api('/me/notifications/config', { method: 'GET' }, notificationsConfigSchema);
    return !!config.vapidPublicKey;
  } catch {
    return false;
  }
}

/** Whether this browser supports service-worker push at all. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ?? navigator.serviceWorker.register('/sw.js');
}

/** True when a push subscription already exists (so the toggle can render the right state). */
export async function isPushEnabled(): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    return (await reg.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

/**
 * Re-associate an existing browser subscription with the currently authenticated user. On a
 * shared browser, user B logging in already has A's granted subscription, so the bell shows
 * "enabled" and B never re-subscribes — leaving A's server row pointing at this browser. Calling
 * this on login re-PUTs the subscription, and the server detaches the endpoint from A. No-op when
 * push is unsupported, not yet granted, or no subscription exists.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const config = await api('/me/notifications/config', { method: 'GET' }, notificationsConfigSchema);
    if (!config.vapidPublicKey) return; // web push not configured server-side
    const reg = await navigator.serviceWorker.getRegistration();
    const existing = reg ? await reg.pushManager.getSubscription() : null;
    if (!reg || !existing) return; // nothing to re-bind (user hasn't enabled push here)
    // If the server's VAPID key rotated, the existing subscription is bound to the old key and its
    // sends would be rejected — recreate it before binding. Otherwise reuse it.
    const sub = await ensureSubscription(reg, existing, config.vapidPublicKey);
    // Re-binding this endpoint to the current user is a privacy step (the previous owner keeps
    // receiving pushes on this browser until it lands), so retry a transient PUT with backoff.
    const body = sub.toJSON();
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        await api('/me/push-subscription', { method: 'PUT', body });
        return;
      } catch {
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1_000 * 2 ** attempt));
      }
    }
  } catch {
    // ignore — a failed re-sync leaves the prior association until the next attempt
  }
}

/** Register the SW, request permission, subscribe, and persist the subscription server-side. */
export async function enablePush(): Promise<EnableResult> {
  if (!isPushSupported()) return 'unsupported';
  try {
    const config = await api(
      '/me/notifications/config',
      { method: 'GET' },
      notificationsConfigSchema,
    );
    if (!config.vapidPublicKey) return 'unconfigured';

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'blocked';

    const reg = await registration();
    await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub = await ensureSubscription(reg, existing, config.vapidPublicKey);

    try {
      await api('/me/push-subscription', { method: 'PUT', body: sub.toJSON() });
    } catch (err) {
      // The browser subscription exists but the server never stored it. Leaving it would make
      // isPushEnabled() report "enabled" on the next load while the server has no endpoint and
      // nothing retries — so tear the local subscription down to keep client and server in sync.
      try {
        await sub.unsubscribe();
      } catch {
        /* already gone */
      }
      throw err;
    }
    return 'enabled';
  } catch {
    return 'failed';
  }
}

/** base64url of a subscription's `applicationServerKey`, or null if absent (for VAPID-key compare). */
function appServerKeyB64(sub: PushSubscription): string | null {
  const key = sub.options?.applicationServerKey;
  if (!key) return null;
  const bytes = new Uint8Array(key);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Reuse `existing` if it's bound to `vapidKey`; otherwise (rotated/absent) unsubscribe + resubscribe. */
async function ensureSubscription(
  reg: ServiceWorkerRegistration,
  existing: PushSubscription | null,
  vapidKey: string,
): Promise<PushSubscription> {
  if (existing) {
    if (appServerKeyB64(existing) === vapidKey) return existing;
    try {
      await existing.unsubscribe();
    } catch {
      /* already gone */
    }
  }
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
  });
}

/** Unsubscribe locally and clear the stored subscription server-side. */
export async function disablePush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) await sub.unsubscribe();
  } catch {
    // ignore — we still clear the server copy below
  }
  try {
    await api('/me/push-subscription', { method: 'DELETE' });
  } catch {
    // ignore — a stale server copy is cleared on the next dead-send anyway
  }
}

/** Decode a base64url VAPID key into the `Uint8Array` `applicationServerKey` the API wants. */
function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
